import { ICustomer } from '@monofront/types';
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useReducer,
  useMemo,
} from 'react';
import { IAuthCRUD, ICustomerCRUD, IUnsubscribe } from '../interfaces';
import { areObjectsEqual, omit } from '../utils';

/** Interface of the user authentication service (firebase for now) */
export interface IAuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  /** get the json web token */
  getIdToken: (forceRefresh?: boolean | undefined) => Promise<string>;
}

/** interface of the state maintained by the Auth context */
export type IAuthState = {
  authUser: IAuthUser | null;
  /** flag to indicate that the authentication is still loading when the user is accessing the app */
  isLoading: boolean;
  /** flag to indicate that the customer is still loading when the user is accessing the app */
  isCustomerLoading: boolean;
  /** flag to indicate if the user as a subscription to La Fourche */
  isMember: boolean;
  /** the user id of the OMS (shopify) */
  omsId: number | null;
  /** the customer information that are in the database */
  customer?: ICustomer | null;
  /** a flag to indicate that the user is authenticated */
  isAuthenticated: boolean;
  /** a flag to indicate that the user is connected with google or facebook */
  isFromSocialMedia: boolean;
  /** a flag to indicate that the user is either not connected or connected with no subscription  */
  isMemberProspect: undefined | boolean;
  /** user is ready when he can subscribe */
  isUserReady: boolean;
  /** a flag which indicates if a user is voluntarily disconnecting (after clicked on log out button, use to prevent unwanted redirections) */
  isLogoutPending: boolean;
  /** set a flag which indicates if a user is voluntarily disconnecting (after clicked on log out button, use to prevent unwanted redirections) */
  setIsLogoutPending?: React.Dispatch<React.SetStateAction<boolean>>;
};

export type UseAuthenticationHook = () => IAuthState;

export interface IAuthContextCreator {
  /** Context provider component */
  AuthProvider: React.FC<{ children?: React.ReactNode }>;
  /** hook to retrieve the state */
  useAuthentication: UseAuthenticationHook;
}

const defaultUser: IAuthState = {
  authUser: null,
  isLoading: true,
  isCustomerLoading: true,
  isMember: false,
  omsId: null,
  customer: null,
  isAuthenticated: false,
  isFromSocialMedia: false,
  isMemberProspect: true,
  isUserReady: false,
  isLogoutPending: false,
  setIsLogoutPending: undefined,
};

const authReducer = (state: IAuthState, action: Partial<IAuthState>) => {
  if ('customer' in action && action.customer && state.customer) {
    // As it is, if there is `customer` in an action, it means there is only `customer`.
    if (areObjectsEqual(action.customer, state.customer)) {
      return state;
    }
  }

  const newState = {
    ...state,
    ...action,
    isCustomerLoading:
      // If customer in action and there is a corresponding authUser, set isCustomerLoading to false
      'customer' in action
        ? 'authUser' in action
          ? action.authUser?.uid !== action.customer?.uid
          : state.authUser?.uid !== action.customer?.uid
        : // If authUser is different, set isCustomerLoading to true
        'authUser' in action
        ? Boolean(action.authUser) &&
          state.authUser?.uid !== action.authUser?.uid
        : // Else: get the current one from the state
          state.isCustomerLoading,
    isUserReady: Boolean(
      'customer' in action ? action.customer?.omsId : state.customer?.omsId,
    ),
    omsId: 'customer' in action ? action.customer?.omsId ?? null : state.omsId,
    //TODO this looks like isMemberProspect could be simplified with !customer.member
    isMemberProspect:
      'customer' in action && action.customer
        ? !action.customer.member
        : state.isMemberProspect,
  };

  if (areObjectsEqual(state, newState)) {
    return state;
  }

  return newState;
};

/**
 * Factory function that create the AuthContext
 * @param authCRUD CRUD to be injected in the app using this context
 * @param customerCRUD CRUD to be injected in the app using this context
 * @returns AuthContext
 */
export const createAuthContext = (
  authCRUD: IAuthCRUD,
  customerCRUD: ICustomerCRUD,
): IAuthContextCreator => {
  const AuthContext = createContext<IAuthState>({
    ...defaultUser,
  });

  const AuthProvider = ({ children }: { children?: React.ReactNode }) => {
    const [isLogoutPending, setIsLogoutPending] = useState(false);
    const [state, dispatch] = useReducer(authReducer, {
      ...defaultUser,
      isLogoutPending: isLogoutPending,
      setIsLogoutPending: setIsLogoutPending,
    });

    // This listener is only responsible for managing the authentication status
    useEffect(() => {
      let unlisten: IUnsubscribe;

      (async function listenFirebase() {
        const authenticatedCallback = ({
          authUser,
          isMember,
          isFromSocialMedia,
        }) => {
          dispatch({
            authUser,
            isLoading: false,
            isMember: Boolean(isMember),
            isAuthenticated: Boolean(authUser),
            isFromSocialMedia,
            isMemberProspect: !isMember,
          });
        };
        const unauthenticatedCallback = () => {
          dispatch({
            ...defaultUser,
            isLoading: false,
            isCustomerLoading: false,
          });
        };

        unlisten = await authCRUD.listen(
          authenticatedCallback,
          unauthenticatedCallback,
        );
      })();

      return () => {
        if (unlisten) {
          unlisten();
        }
      };
    }, []);

    // This listener is only responsible for updating customer data
    useEffect(() => {
      if (!state.authUser) {
        return;
      }

      const unlisten = customerCRUD.listen(state.authUser.uid, (customer) => {
        dispatch({
          customer: omit(customer, 'updatedAt', 'createdAt'),
        });

        authCRUD.forceUpdate();
      });

      return () => {
        if (unlisten) {
          unlisten();
        }
      };
    }, [state.authUser?.uid]);

    return (
      <AuthContext.Provider
        value={useMemo(
          () => ({ ...state, isLogoutPending, setIsLogoutPending }),
          [state, isLogoutPending, setIsLogoutPending],
        )}
      >
        {children}
      </AuthContext.Provider>
    );
  };

  return {
    AuthProvider,
    useAuthentication: () => useContext(AuthContext),
  };
};
